import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { byPrefixAndName } from '@awesome.me/kit-f089350b06/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Vendor hooks.
import { useTranslation } from 'react-i18next';

// React-shared.
import Gravatar from '@exonet/react-shared/lib/Elements/Gravatar';

// Hooks.
import useToggleMenu from 'app/client/utilities/hooks/useToggleMenu';
import useSessionEnd from './hooks/useSessionEnd';

// Local components.
import { NavLink } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import ToggleTheme from './components/ToggleTheme';
import ToggleLanguage from './components/ToggleLanguage';

// Store.
import userConstants from 'app/client/store/reducers/user/constants';

// Styled.
import * as S from './styled';

const Settings = ({
  user,
  language,
  actions: { setLanguage },
  authenticated,
  authorized,
  canSwitchCustomer,
  settingsLoading = false,
}) => {
  // Translations.
  const { t } = useTranslation();

  // Toggle.
  const { ref, open, handleToggle, handleClose } = useToggleMenu();

  const [switchCustomer, switching] = useSessionEnd(
    '/auth/close',
    userConstants.closeCustomer,
    '/user/customers',
    handleClose,
  );
  const [logout, loggingOut] = useSessionEnd(
    '/auth/logout',
    userConstants.logout,
    '/user/login',
    handleClose,
  );

  // Handler.
  const handleLanguageClick = useCallback(
    (payload) => {
      setLanguage(payload);
      handleClose();
    },
    [setLanguage, handleClose],
  );

  return (
    <S.Wrapper ref={ref}>
      <S.Toggle onClick={handleToggle} id="user-settings-menu">
        <S.Icon>
          <FontAwesomeIcon icon={byPrefixAndName.fas['user-gear']} size="lg" />
        </S.Icon>
      </S.Toggle>
      {open && (
        <S.Menu>
          {user?.emailAddress && (
            <S.Item seperatorBottom>
              <S.User>
                <Gravatar emailAddress={user.emailAddress} />
                <div>
                  <span>{user.name}</span>
                  <span>{user.emailAddress}</span>
                </div>
              </S.User>
            </S.Item>
          )}
          <S.Header>{t('core:menu.theme.title')}</S.Header>
          <S.Item>
            <ToggleTheme disabled={settingsLoading} />
          </S.Item>
          <S.Header>{t('core:menu.language')}</S.Header>
          <S.Item>
            <ToggleLanguage
              value={language}
              action={handleLanguageClick}
              disabled={settingsLoading}
            />
          </S.Item>
          {authorized && (
            <>
              <S.Header>{t('core:menu.settings')}</S.Header>
              {canSwitchCustomer && (
                <S.Item>
                  <NavLink to="/settings/preferences">
                    <FontAwesomeIcon icon={byPrefixAndName.fal.sliders} />
                    {t('core:menu.items.settings.preferences')}
                  </NavLink>
                </S.Item>
              )}
              <S.Item>
                <NavLink to="/settings/security">
                  <FontAwesomeIcon icon={byPrefixAndName.fal.lock} />
                  {t('core:menu.items.settings.security')}
                </NavLink>
              </S.Item>
              {/* <Restricted to="access_tokens.read" noError>
                <S.Item>
                  <NavLink to="/settings/api_access">
                    <FontAwesomeIcon icon={byPrefixAndName.fal['gear-code']} />
                    {t('core:menu.items.settings.apiAccess')}
                  </NavLink>
                </S.Item>
              </Restricted> */}
            </>
          )}
          <S.Footer>
            {authorized && canSwitchCustomer && (
              <S.Item seperatorTop>
                <S.Action type="button" onClick={switchCustomer} disabled={switching}>
                  <FontAwesomeIcon icon={byPrefixAndName.fal['arrow-right-arrow-left']} />
                  {t('core:menu.items.settings.switchCustomer')}
                </S.Action>
              </S.Item>
            )}
            {authenticated && (
              <S.Item className="navbar-logout" seperatorTop={!canSwitchCustomer}>
                <S.Action type="button" onClick={logout} disabled={loggingOut}>
                  <FontAwesomeIcon icon={byPrefixAndName.fal['right-from-bracket']} />
                  {t('core:menu.items.user.logout')}
                </S.Action>
              </S.Item>
            )}
          </S.Footer>
        </S.Menu>
      )}
      <Tooltip
        anchorSelect="#user-settings-menu"
        border="1px solid var(--rt-border-color, transparent)"
        place="top-end"
      >
        {t('core:menu.userSettings')}
      </Tooltip>
    </S.Wrapper>
  );
};

Settings.propTypes = {
  user: PropTypes.shape({
    name: PropTypes.string,
    emailAddress: PropTypes.string,
  }),
  language: PropTypes.string.isRequired,
  actions: PropTypes.shape({
    setLanguage: PropTypes.func.isRequired,
  }).isRequired,
  authenticated: PropTypes.bool.isRequired,
  authorized: PropTypes.bool.isRequired,
  canSwitchCustomer: PropTypes.bool,
  settingsLoading: PropTypes.bool,
};

export default Settings;
